import Link from "next/link";
import {
	AbstractParentNode,
	HeadingNode,
	TextNodeType,
	TextContentNode,
} from "./CmsTypes";
import {useContext} from "react";
import {TextContentContext} from "./TextSection";
import TextNodeComponent from "./TextNodeComponent";
import {collateText} from "./cmsHelpers";
import StrapiImage from "../StrapiImage";
import {
	AbstractParentBlock,
	Block,
	HeadingBlock,
	TextBlockType,
} from "@/shared/strapiTypes/common/Blocks";

const NodeChildren = ({
	node,
}: {
	node: AbstractParentNode | AbstractParentBlock;
}) => {
	return (
		<>
			{node.children.map((child, i) => (
				<TextContent key={i} node={child} />
			))}
		</>
	);
};

interface HeadingProps {
	node: HeadingNode | HeadingBlock;
}

const Heading: React.FC<HeadingProps> = ({node}) => {
	switch (node.level) {
		case 1:
			return (
				<h1 className="mb-6">
					<NodeChildren node={node} />
				</h1>
			);
		case 2:
			return (
				<h2 className="mb-6">
					<NodeChildren node={node} />
				</h2>
			);
		case 3:
			return (
				<h3 className="mb-4">
					<NodeChildren node={node} />
				</h3>
			);
		case 4:
			return (
				<h4 className="mb-4">
					<NodeChildren node={node} />
				</h4>
			);
		case 5:
			return (
				<h5 className="mb-4">
					<NodeChildren node={node} />
				</h5>
			);
		case 6:
			return (
				<h6 className="mb-4">
					<NodeChildren node={node} />
				</h6>
			);
	}
};

export interface TextContentProps {
	node: TextContentNode | Block;
}

/**
 * Component which renders a single node of the Strapi rich text data structure,
 * which is output by the API when you include a "Rich text (Blocks)" data field.
 *
 * See TextBody.stories.tsx for an example.
 */
const TextContent: React.FC<TextContentProps> = ({node}) => {
	const {classNamesByType} = useContext(TextContentContext);
	switch (node.type) {
		case TextNodeType.TEXT:
		case TextBlockType.TEXT:
			return <TextNodeComponent node={node} />;
		case TextNodeType.HEADING:
		case TextBlockType.HEADING:
			return <Heading node={node} />;
		case TextNodeType.LIST:
		case TextBlockType.LIST:
			if (node.format === "unordered") {
				return (
					<ul className="mb-4 last:mb-0">
						<NodeChildren node={node} />
					</ul>
				);
			} else {
				return (
					<ol className="mb-4 last:mb-0">
						<NodeChildren node={node} />
					</ol>
				);
			}
		case TextNodeType.LIST_ITEM:
		case TextBlockType.LIST_ITEM:
			return (
				<li className={classNamesByType?.[TextNodeType.LIST_ITEM]}>
					<NodeChildren node={node} />
				</li>
			);
		case TextNodeType.PARAGRAPH:
		case TextBlockType.PARAGRAPH:
			return (
				<p className="mb-4 leading-6 last:mb-0">
					<NodeChildren node={node} />
				</p>
			);
		case TextNodeType.LINK:
		case TextBlockType.LINK:
			// make sure the link has text content before creating it
			const linkText = collateText(node.children);
			if (!linkText.trim()) {
				console.warn(`Omitting link without visible text ${node.url}`);
				return null;
			}

			return (
				<Link href={node.url} className="text-hyperlink">
					<NodeChildren node={node} />
				</Link>
			);
		case TextNodeType.QUOTE:
		case TextBlockType.QUOTE:
			// Doesn't do anything yet
			return (
				<blockquote>
					<NodeChildren node={node} />
				</blockquote>
			);
		case TextNodeType.CODE:
		case TextBlockType.CODE:
			// Doesn't do anything besides change font yet
			return (
				<code>
					<NodeChildren node={node} />
				</code>
			);
		case TextNodeType.IMAGE:
			return <StrapiImage image={{attributes: node.image}} />;
		default:
			return (
				<div>
					<NodeChildren node={node} />
				</div>
			);
	}
};

export default TextContent;
